import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../redux/root-reducer';
import { campaignThreadTypingReceived } from '../redux/buzzbridge/actions';
import type { ICampaignThreadTypingState } from '../redux/buzzbridge/campaignThreadMessage.types';

/** Hide indicator this long after the last typing pulse (server or heartbeat). */
export const REMOTE_TYPING_IDLE_MS = 3000;

function isLikelySelfTyping(
    remoteTyping: ICampaignThreadTypingState,
    currentUserId?: string,
    counterpartDisplayName?: string,
    counterpartUserIds: string[] = [],
): boolean {
    const uid = remoteTyping.userId?.trim();
    const selfId = String(currentUserId ?? '').trim();
    if (!uid || !selfId || uid !== selfId) return false;

    const remoteName = remoteTyping.displayName?.trim();
    const counterpartName = counterpartDisplayName?.trim();
    if (remoteName && counterpartName && remoteName.toLowerCase() === counterpartName.toLowerCase()) {
        /** Backend sometimes tags the wrong userId — trust displayName when it matches the other party. */
        return false;
    }

    if (counterpartUserIds.some(id => id && String(id) === uid)) {
        return false;
    }

    return true;
}

function shouldShowRemoteTyping(
    remoteTyping: ICampaignThreadTypingState,
    currentUserId?: string,
    counterpartDisplayName?: string,
    counterpartUserIds: string[] = [],
    brandMode = false,
    localTyping = false,
): boolean {
    if (localTyping) return false;

    const uid = remoteTyping.userId?.trim();
    const selfId = String(currentUserId ?? '').trim();
    const remoteName = remoteTyping.displayName?.trim().toLowerCase();
    const counterpartName = counterpartDisplayName?.trim().toLowerCase();

    if (uid && selfId && uid === selfId) {
        /** Same userId as host — only show when displayName clearly identifies the creator (backend mis-tag). */
        return Boolean(remoteName && counterpartName && remoteName === counterpartName);
    }

    if (brandMode) {
        return true;
    }

    return !isLikelySelfTyping(remoteTyping, currentUserId, counterpartDisplayName, counterpartUserIds);
}

/** Remote typing for a campaign thread + idle expiry after the last pulse. */
export function useCampaignThreadRemoteTyping(
    participantId: string | undefined,
    currentUserId?: string,
    counterpartDisplayName?: string,
    counterpartUserIds: string[] = [],
    brandMode = false,
    localTyping = false,
): { remoteTyping: ICampaignThreadTypingState | null; showRemoteTyping: boolean } {
    const dispatch = useDispatch();
    const pid = participantId?.trim() ?? '';
    const remoteTyping = useSelector((state: RootState) =>
        pid ? state.buzzbridge.campaignThreadTyping?.[pid] ?? null : null,
    );
    const typingAt = useSelector((state: RootState) => (pid ? state.buzzbridge.campaignThreadTypingAt?.[pid] ?? 0 : 0));

    useEffect(() => {
        if (!pid || !remoteTyping || !typingAt) return undefined;
        const timer = setTimeout(() => {
            dispatch(campaignThreadTypingReceived({ participantId: pid, typing: null }));
        }, REMOTE_TYPING_IDLE_MS);
        return () => clearTimeout(timer);
    }, [dispatch, pid, remoteTyping, typingAt]);

    const showRemoteTyping = Boolean(
        remoteTyping &&
            shouldShowRemoteTyping(
                remoteTyping,
                currentUserId,
                counterpartDisplayName,
                counterpartUserIds,
                brandMode,
                localTyping,
            ),
    );

    return { remoteTyping, showRemoteTyping };
}
